/**
 * FERP Portal - Approval Inbox Page (DRR-233: Track C)
 *
 * EXECUTE Phase: Focused inbox for pending approval actions.
 * Provides quick decision-making UI for approvers with:
 * - Pending approvals filtered by current user
 * - Priority badge and expiration indicators
 * - Quick approve/reject actions
 * - Escalation support for out-of-scope items
 *
 * FERP Principles:
 * - Life-critical decisions require clear context and audit trail
 * - Backend-driven: uses listApprovalsForApprover + getPendingApprovalsCount
 * - Immutable audit trails for all approval decisions
 */

import { useCallback, useMemo, useState } from 'react';
import {
  Container,
  Title,
  Stack,
  Card,
  Group,
  Badge,
  Text,
  Button,
  Loader,
  Alert,
  Tabs,
  Paper,
  ActionIcon,
  Tooltip,
  TextInput,
  Modal,
  Textarea,
  SimpleGrid,
  Divider,
  ThemeIcon,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconInbox,
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconArrowUp,
  IconClock,
  IconMessage,
  IconRefresh,
  IconFilter,
} from '@tabler/icons-react';
import { formatDistanceToNow, isPast, parseISO } from 'date-fns';

import { useDevActorId } from '@/hooks/useDevActorId';
import { useTenantId } from '@/auth/AuthContext';
import {
  useListApprovalsForApproverQuery,
  useGetPendingApprovalsCountQuery,
  useRecordApprovalDecisionMutation,
  useEscalateApprovalMutation,
  useAddApprovalCommentMutation,
  GqlApprovalDecision,
  GqlApprovalStatus,
} from '@/generated/graphql';
import type { Approval } from '@/generated/graphql';

// Priority color mapping
const priorityColors: Record<string, string> = {
  CRITICAL: 'red',
  HIGH: 'orange',
  MEDIUM: 'yellow',
  LOW: 'blue',
};

// Status color mapping
const statusColors: Record<string, string> = {
  PENDING: 'blue',
  IN_REVIEW: 'cyan',
  APPROVED: 'green',
  REJECTED: 'red',
  ESCALATED: 'orange',
  EXPIRED: 'gray',
  REVISION_REQUESTED: 'violet',
};

interface ApprovalCardProps {
  approval: Approval;
  onApprove: (id: string, reason: string) => void;
  onReject: (id: string, reason: string) => void;
  onEscalate: (id: string, escalateTo: string, reason: string) => void;
  onAddComment: (id: string, comment: string) => void;
}

function ApprovalCard({ approval, onApprove, onReject, onEscalate, onAddComment }: ApprovalCardProps) {
  const [commentModalOpened, { open: openCommentModal, close: closeCommentModal }] = useDisclosure(false);
  const [decisionModalOpened, { open: openDecisionModal, close: closeDecisionModal }] = useDisclosure(false);
  const [escalateModalOpened, { open: openEscalateModal, close: closeEscalateModal }] = useDisclosure(false);

  const [decisionType, setDecisionType] = useState<'approve' | 'reject'>('approve');
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [escalateTo, setEscalateTo] = useState('');

  const isExpiringSoon = approval.expiresAt
    ? new Date(approval.expiresAt).getTime() - Date.now() < 24 * 60 * 60 * 1000
    : false;
  const isExpired = approval.expiresAt ? isPast(parseISO(approval.expiresAt)) : false;

  const handleDecision = () => {
    if (decisionType === 'approve') {
      onApprove(approval.id, reason);
    } else {
      onReject(approval.id, reason);
    }
    closeDecisionModal();
    setReason('');
  };

  const handleEscalate = () => {
    onEscalate(approval.id, escalateTo, reason);
    closeEscalateModal();
    setReason('');
    setEscalateTo('');
  };

  const handleAddComment = () => {
    onAddComment(approval.id, comment);
    closeCommentModal();
    setComment('');
  };

  const openApproveModal = () => {
    setDecisionType('approve');
    openDecisionModal();
  };

  const openRejectModal = () => {
    setDecisionType('reject');
    openDecisionModal();
  };

  return (
    <>
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="sm">
          {/* Header */}
          <Group justify="space-between">
            <Group gap="xs">
              <Badge color={statusColors[approval.status] ?? 'gray'} variant="light">
                {approval.status}
              </Badge>
              <Badge color={priorityColors[approval.priority] ?? 'gray'} variant="dot">
                {approval.priority}
              </Badge>
              {isExpiringSoon && !isExpired && (
                <Badge color="orange" variant="filled" leftSection={<IconClock size={12} />}>
                  Expiring Soon
                </Badge>
              )}
              {isExpired && (
                <Badge color="red" variant="filled">
                  Expired
                </Badge>
              )}
            </Group>
            <Text size="xs" c="dimmed">
              {approval.createdAt && formatDistanceToNow(parseISO(approval.createdAt as string), { addSuffix: true })}
            </Text>
          </Group>

          {/* Title and Type */}
          <div>
            <Text fw={600} size="lg">{approval.approvalName ?? `Approval ${approval.id}`}</Text>
            <Text size="sm" c="dimmed">
              {approval.approvalType} - {approval.approverRole}
            </Text>
          </div>

          {/* Entity Context */}
          {approval.entityType && (
            <Paper p="xs" bg="gray.0" radius="sm">
              <Text size="sm">
                <Text span fw={500}>Entity:</Text> {approval.entityType}
              </Text>
            </Paper>
          )}

          {/* Progress */}
          <Group gap="xs">
            <Text size="sm" c="dimmed">Approvals:</Text>
            <Badge variant="outline" color="blue">
              {approval.currentApprovalCount ?? 0} / {approval.requiredApprovals ?? 1}
            </Badge>
            <Text size="sm" c="dimmed">Strategy: {approval.approvalStrategy}</Text>
          </Group>

          {/* Expiration */}
          {approval.expiresAt && (
            <Text size="xs" c={isExpiringSoon ? 'orange' : 'dimmed'}>
              Expires: {formatDistanceToNow(parseISO(approval.expiresAt), { addSuffix: true })}
            </Text>
          )}

          <Divider />

          {/* Actions */}
          <Group justify="space-between">
            <Group gap="xs">
              <Button
                size="xs"
                color="green"
                leftSection={<IconCheck size={14} />}
                onClick={openApproveModal}
                disabled={approval.status !== 'PENDING' && approval.status !== 'IN_REVIEW'}
              >
                Approve
              </Button>
              <Button
                size="xs"
                color="red"
                variant="light"
                leftSection={<IconX size={14} />}
                onClick={openRejectModal}
                disabled={approval.status !== 'PENDING' && approval.status !== 'IN_REVIEW'}
              >
                Reject
              </Button>
              <Button
                size="xs"
                color="orange"
                variant="subtle"
                leftSection={<IconArrowUp size={14} />}
                onClick={openEscalateModal}
                disabled={approval.status !== 'PENDING' && approval.status !== 'IN_REVIEW'}
              >
                Escalate
              </Button>
            </Group>
            <Tooltip label="Add Comment">
              <ActionIcon variant="subtle" onClick={openCommentModal}>
                <IconMessage size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Stack>
      </Card>

      {/* Decision Modal */}
      <Modal
        opened={decisionModalOpened}
        onClose={closeDecisionModal}
        title={decisionType === 'approve' ? 'Approve Request' : 'Reject Request'}
        centered
      >
        <Stack>
          <Text size="sm" c="dimmed">
            {decisionType === 'approve'
              ? 'Provide a reason for approval (optional):'
              : 'Provide a reason for rejection (required):'}
          </Text>
          <Textarea
            placeholder={decisionType === 'approve' ? 'Approval reason...' : 'Rejection reason...'}
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            minRows={3}
            required={decisionType === 'reject'}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeDecisionModal}>Cancel</Button>
            <Button
              color={decisionType === 'approve' ? 'green' : 'red'}
              onClick={handleDecision}
              disabled={decisionType === 'reject' && !reason.trim()}
            >
              {decisionType === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Escalate Modal */}
      <Modal opened={escalateModalOpened} onClose={closeEscalateModal} title="Escalate Approval" centered>
        <Stack>
          <TextInput
            label="Escalate To"
            placeholder="Enter role or actor ID..."
            value={escalateTo}
            onChange={(e) => setEscalateTo(e.currentTarget.value)}
            required
          />
          <Textarea
            label="Reason"
            placeholder="Why is this being escalated?"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            minRows={2}
            required
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeEscalateModal}>Cancel</Button>
            <Button
              color="orange"
              onClick={handleEscalate}
              disabled={!escalateTo.trim() || !reason.trim()}
            >
              Escalate
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Comment Modal */}
      <Modal opened={commentModalOpened} onClose={closeCommentModal} title="Add Comment" centered>
        <Stack>
          <Textarea
            placeholder="Your comment..."
            value={comment}
            onChange={(e) => setComment(e.currentTarget.value)}
            minRows={3}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeCommentModal}>Cancel</Button>
            <Button onClick={handleAddComment} disabled={!comment.trim()}>
              Add Comment
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

export function ApprovalInboxPage() {
  const tenantId = useTenantId() ?? '';
  const { actorId, actorName, loading: actorLoading } = useDevActorId();
  const [activeTab, setActiveTab] = useState<string | null>('pending');

  // Queries
  const {
    data: approvalsData,
    loading: approvalsLoading,
    error: approvalsError,
    refetch: refetchApprovals,
  } = useListApprovalsForApproverQuery({
    variables: { tenantId, approverId: actorId ?? '', limit: 100 },
    skip: !actorId || !tenantId,
  });

  const { data: countData, refetch: refetchCount } = useGetPendingApprovalsCountQuery({
    variables: { tenantId, approverId: actorId ?? '' },
    skip: !actorId || !tenantId,
  });

  // Mutations
  const [recordDecision] = useRecordApprovalDecisionMutation();
  const [escalateApproval] = useEscalateApprovalMutation();
  const [addComment] = useAddApprovalCommentMutation();

  // Filter approvals by status
  const approvals = useMemo(() => {
    const items = approvalsData?.listApprovalsForApprover?.items ?? [];
    if (activeTab === 'pending') {
      return items.filter((a) => a.status === GqlApprovalStatus.PENDING || a.status === GqlApprovalStatus.IN_REVIEW);
    }
    if (activeTab === 'escalated') {
      return items.filter((a) => a.status === GqlApprovalStatus.ESCALATED);
    }
    if (activeTab === 'completed') {
      return items.filter((a) => a.status === GqlApprovalStatus.APPROVED || a.status === GqlApprovalStatus.REJECTED);
    }
    return items;
  }, [approvalsData, activeTab]);

  const pendingCount = countData?.getPendingApprovalsCount ?? 0;
  const escalatedCount = useMemo(
    () => approvalsData?.listApprovalsForApprover?.items?.filter((a) => a.status === GqlApprovalStatus.ESCALATED).length ?? 0,
    [approvalsData]
  );

  const handleRefresh = useCallback(() => {
    refetchApprovals();
    refetchCount();
  }, [refetchApprovals, refetchCount]);

  const handleApprove = useCallback(
    async (approvalId: string, reason: string) => {
      try {
        await recordDecision({
          variables: {
            tenantId,
            approvalId,
            input: {
              actorId: actorId ?? '',
              actorName: actorName ?? '',
              decision: GqlApprovalDecision.APPROVE,
              reason: reason || undefined,
            },
          },
        });
        notifications.show({
          title: 'Approved',
          message: 'Approval decision recorded successfully',
          color: 'green',
        });
        handleRefresh();
      } catch (err) {
        notifications.show({
          title: 'Error',
          message: `Failed to approve: ${err}`,
          color: 'red',
        });
      }
    },
    [tenantId, actorId, actorName, recordDecision, handleRefresh]
  );

  const handleReject = useCallback(
    async (approvalId: string, reason: string) => {
      try {
        await recordDecision({
          variables: {
            tenantId,
            approvalId,
            input: {
              actorId: actorId ?? '',
              actorName: actorName ?? '',
              decision: GqlApprovalDecision.REJECT,
              reason,
            },
          },
        });
        notifications.show({
          title: 'Rejected',
          message: 'Rejection recorded successfully',
          color: 'orange',
        });
        handleRefresh();
      } catch (err) {
        notifications.show({
          title: 'Error',
          message: `Failed to reject: ${err}`,
          color: 'red',
        });
      }
    },
    [tenantId, actorId, actorName, recordDecision, handleRefresh]
  );

  const handleEscalate = useCallback(
    async (approvalId: string, escalateTo: string, reason: string) => {
      try {
        await escalateApproval({
          variables: {
            tenantId,
            approvalId,
            input: { escalateTo, reason },
          },
        });
        notifications.show({
          title: 'Escalated',
          message: 'Approval escalated successfully',
          color: 'orange',
        });
        handleRefresh();
      } catch (err) {
        notifications.show({
          title: 'Error',
          message: `Failed to escalate: ${err}`,
          color: 'red',
        });
      }
    },
    [tenantId, escalateApproval, handleRefresh]
  );

  const handleAddComment = useCallback(
    async (approvalId: string, commentText: string) => {
      try {
        await addComment({
          variables: {
            tenantId,
            approvalId,
            input: {
              actorId: actorId ?? '',
              comment: commentText,
            },
          },
        });
        notifications.show({
          title: 'Comment Added',
          message: 'Your comment has been added',
          color: 'blue',
        });
        handleRefresh();
      } catch (err) {
        notifications.show({
          title: 'Error',
          message: `Failed to add comment: ${err}`,
          color: 'red',
        });
      }
    },
    [tenantId, actorId, addComment, handleRefresh]
  );

  if (!actorId && !actorLoading) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="No Actor Found">
          Unable to determine your actor identity. Please ensure you are logged in.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <ThemeIcon size="xl" variant="light" color="blue">
              <IconInbox size={24} />
            </ThemeIcon>
            <div>
              <Title order={2}>Approval Inbox</Title>
              <Text size="sm" c="dimmed">
                Pending approvals requiring your action
              </Text>
            </div>
          </Group>
          <Group>
            {actorName && (
              <Badge variant="light" color="blue" size="lg">
                {actorName}
              </Badge>
            )}
            <Tooltip label="Refresh">
              <ActionIcon variant="light" onClick={handleRefresh} loading={approvalsLoading}>
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        {/* Error State */}
        {approvalsError && (
          <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error">
            {approvalsError.message}
          </Alert>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="pending" leftSection={<IconClock size={16} />}>
              Pending
              {pendingCount > 0 && (
                <Badge size="xs" ml={8} color="blue" variant="filled">
                  {pendingCount}
                </Badge>
              )}
            </Tabs.Tab>
            <Tabs.Tab value="escalated" leftSection={<IconArrowUp size={16} />}>
              Escalated
              {escalatedCount > 0 && (
                <Badge size="xs" ml={8} color="orange" variant="filled">
                  {escalatedCount}
                </Badge>
              )}
            </Tabs.Tab>
            <Tabs.Tab value="completed" leftSection={<IconCheck size={16} />}>
              Completed
            </Tabs.Tab>
            <Tabs.Tab value="all" leftSection={<IconFilter size={16} />}>
              All
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>

        {/* Loading State */}
        {approvalsLoading && (
          <Group justify="center" py="xl">
            <Loader size="lg" />
          </Group>
        )}

        {/* Empty State */}
        {!approvalsLoading && approvals.length === 0 && (
          <Paper p="xl" ta="center" withBorder>
            <IconInbox size={48} stroke={1} color="gray" />
            <Text size="lg" fw={500} mt="md">
              No approvals found
            </Text>
            <Text size="sm" c="dimmed">
              {activeTab === 'pending'
                ? 'You have no pending approvals requiring action.'
                : `No ${activeTab} approvals to display.`}
            </Text>
          </Paper>
        )}

        {/* Approval Cards */}
        {!approvalsLoading && approvals.length > 0 && (
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            {approvals.map((approval) => (
              <ApprovalCard
                key={approval.id}
                approval={approval as Approval}
                onApprove={handleApprove}
                onReject={handleReject}
                onEscalate={handleEscalate}
                onAddComment={handleAddComment}
              />
            ))}
          </SimpleGrid>
        )}
      </Stack>
    </Container>
  );
}

export default ApprovalInboxPage;
